<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  useFilter,
  VisuallyHidden,
} from 'reka-ui'
import { ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxItemIndicator, ComboboxRoot } from 'reka-ui'

import ArrowDownSmallIcon from '@/assets/Icons/ArrowDownSmallIcon.svg'
import CloseIcon14 from '@/assets/Icons/CloseIcon14.svg'
import CheckmarkIcon from '@/assets/Icons/CheckmarkIcon.svg'
import HSeparator from '@/components/HSeparator.vue'

defineProps<{
  teleportTarget: string
}>()

const { t, availableLocales } = useI18n()

const searchTerm = ref('')
const isOpen = ref(false)

const { startsWith } = useFilter({ sensitivity: 'base' })
const filteredLangs = computed(() =>
  availableLocales.filter((p) => startsWith(p, searchTerm.value) || startsWith(t(`languages.${p}`), searchTerm.value)),
)

function changeLang() {
  isOpen.value = false
}

//TODO - scroll
</script>

<template>
  <DialogRoot v-model:open="isOpen">
    <DialogTrigger class="dialog-trigger">
      <div class="trigger-container">
        <span class="lang-code">{{ $i18n.locale }}</span>
        <ArrowDownSmallIcon />
      </div>
    </DialogTrigger>
    <DialogPortal :to="teleportTarget">
      <DialogOverlay class="dialog-overlay" />
      <DialogContent class="dialog-content">
        <VisuallyHidden asChild>
          <DialogTitle class="header-title">
            {{ $t('header.languageDialog.title') }}
          </DialogTitle>
        </VisuallyHidden>
        <ComboboxRoot v-model:model-value="$i18n.locale" @update:model-value="changeLang" :ignore-filter="true">
          <div class="header">
            <ComboboxInput class="lang-search" :placeholder="$t('header.languageDialog.title')" v-model="searchTerm" :display-value="() => ''" />
            <DialogClose class="close-button">
              <CloseIcon14 />
            </DialogClose>
          </div>
          <HSeparator />
          <div class="lang-selections">
            <ComboboxEmpty class="lang-search-error">{{ $t('header.languageDialog.searchFailedMessage') }}</ComboboxEmpty>
            <template v-for="locale in filteredLangs" :key="`locale-${locale}`">
              <ComboboxItem :value="locale" class="lang-item">
                <span>
                  <span class="lang-item-code">{{ locale }}</span>
                  <span>{{ $t(`languages.${locale}`) }}</span>
                </span>
                <ComboboxItemIndicator class="ComboboxItemIndicator">
                  <CheckmarkIcon />
                </ComboboxItemIndicator>
              </ComboboxItem>
            </template>
          </div>
        </ComboboxRoot>

        <VisuallyHidden asChild>
          <DialogDescription>{{ $t('header.languageDialog.accesibility.description') }}</DialogDescription>
        </VisuallyHidden>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style lang="scss" scoped>
.dialog-trigger {
  background-color: transparent;
  border: none;
  border-radius: 0;
  padding: 8px;
}

.trigger-container {
  height: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  border-bottom: 2px solid var(--ullib-color-accent-1);
}

.lang-code {
  font-size: var(--ullib-size-font-small);
  line-height: var(--ullib-size-line-spacing-small);
  font-weight: 700;
  color: var(--ullib-color-accent-1);
  text-transform: uppercase;
}

.dialog-overlay {
  position: fixed;
  inset: 0;
}

.dialog-content {
  position: absolute;
  top: calc(100% + 1px);
  right: 0;
  min-width: 240px;
  max-width: 100%;
  padding: 12px;
  background-color: var(--ullib-color-contrast);
  border-radius: 4px;
  box-shadow: 0px 0px 8px var(--ullib-color-shadow);
  display: flex;
  flex-direction: column;
  z-index: 2;
}
.dialog-content:focus {
  outline: none;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 31px;
  width: 100%;
}

.header-title {
  color: var(--ullib-color-accent-1);
  line-height: 18px;
  font-weight: 400;
  align-self: start;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background-color: transparent;
  border: none;
  //TODO - hover
}

.lang-search {
  flex-grow: 1;
  border: none;
  height: 20px;
  &:focus {
    outline: none;
  }
  &::placeholder {
    color: var(--ullib-color-accent-1);
    opacity: 1;
  }
}

.lang-selections {
  margin-top: 8px;
}

.lang-search-error {
  display: flex;
  width: 100%;
  height: initial;
  overflow: hidden;
  line-height: 18px;
  padding-top: 7px;
  padding-left: 12px;
  padding-right: 8px;
  padding-bottom: 7px;
  font-weight: 500;
}

.lang-item {
  height: 32px;
  width: 100%;
  padding-left: 12px;
  padding-right: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-radius: 4px;
  &[data-state='checked'] {
    background-color: var(--ullib-color-hover);
  }
}

.lang-item-code {
  display: inline-block;
  min-width: 33px;
  font-weight: 700;
  text-transform: uppercase;
}
</style>
